import _ from 'lodash';
import React from 'react';

import {formatDate} from 'utils/datetime';

import {BOOKING_TYPE} from 'ducks/bookings';

import Avatar from 'components/Avatar';
import ButtonDropdown from 'components/ButtonDropdown';
import VisibleIf from 'components/VisibleIf';
import BookingStatusTag from 'components/BookingStatusTag';
import {DetailTenantInfo} from 'components/TenantInfo';
import AvatarImageWithText from 'components/AvatarImageWithText';
import {BOOKING_VERIFICATION_METHOD} from 'ducks/bookings/detail';

import {PERMISSIONS} from 'ducks/auth';

import './styles.css';

const BOOKING_TYPES_INITIALS_MAPPING = {
  [BOOKING_TYPE.GUEST]: 'G',
  [BOOKING_TYPE.CORPORATE]: 'C',
  [BOOKING_TYPE.LONG_STAY]: 'LS',
  [BOOKING_TYPE.EXTENDED_STAY]: 'ES'
};

const TenantsBox = ({
  primaryTenant,
  secondaryTenant,
  guarantor,
  bookingType,
  bookingStatus,
  room,
  startDate,
  endDate,
  stayDuration,
  rentPerWeek,
  totalValue,
  cancelBooking,
  undoCheckInTenants,
  bookingOrganisation,
  verificationMethod,
  bookingCanBeCancelled,
  checkOutBookingEarly,
  bookingCanBeCheckedOutEarly,
  undoBookingEarlyCheckout,
  bookingEarlyCheckoutCanBeUndone,
  isEarlyCheckedOut,
  checkedInAt,
  checkedOutAt,
  locationRef
}) => {
  const bookingTypeInitials =
    BOOKING_TYPES_INITIALS_MAPPING[bookingType || BOOKING_TYPE.LONG_STAY];

  return (
    <div className="TenantsBox colab-medium-border-radius">
      <div className="TenantsBox__header">
        <AvatarImageWithText
          initials={bookingTypeInitials}
          text={bookingType}
        />
      </div>
      <div className="colab-sidepadding-2">
        <div className="TenantsBox__primaryTenantInfo colab-centered-v">
          <Avatar
            className="TenantsBox__primaryTenantInfo__avatar"
            url={
              primaryTenant.internalAvatarCropped || primaryTenant.avatarCropped
            }
            initials={primaryTenant.initials}
          />
          <div className="TenantsBox__primaryTenantInfo__name colab-bold">
            {primaryTenant.fullName}
          </div>
          <div className="TenantsBox__primaryTenantInfo__email">
            {primaryTenant.email}
          </div>
          <div className="TenantsBox__primaryTenantInfo__role">
            Primary member
          </div>
          <div className="TenantsBox__infoSection__bookingStatus">
            <BookingStatusTag status={bookingStatus} />
          </div>
        </div>
      </div>

      <div className="TenantsBox__bookingInfo colab-sidepadding-2">
        {secondaryTenant && (
          <div className="TenantsBox__bookingInfo__container">
            <div className="TenantsBox__bookingInfo__heading">
              Secondary member
            </div>
            <DetailTenantInfo tenant={secondaryTenant} />
          </div>
        )}
        {room && (
          <div className="TenantsBox__bookingInfo__container">
            <div className="TenantsBox__bookingInfo__heading">Room</div>
            <div>
              {room.roomNumber}, {room.roomType.name}
            </div>
          </div>
        )}
        <div className="TenantsBox__bookingInfo__container">
          <div className="TenantsBox__bookingInfo__heading">Organisation</div>
          <div>{bookingOrganisation && bookingOrganisation}</div>
          <div>{!bookingOrganisation && '-'}</div>
        </div>
        <div className="TenantsBox__bookingInfo__container">
          <div className="TenantsBox__bookingInfo__heading">Stay duration</div>
          <div>
            {formatDate(startDate)} - {formatDate(endDate)}
          </div>
          <div className="colab-centered-v">
            <div>{stayDuration}</div>
            {isEarlyCheckedOut && (
              <div className="TenantsBox__bookingInfo__earlyCheckedOut colab-centered colab-bold">
                Early checked out on {formatDate(checkedOutAt)}
              </div>
            )}
          </div>
        </div>
        <div className="TenantsBox__bookingInfo__container">
          <div className="TenantsBox__bookingInfo__heading">
            Verification Method
          </div>
          <div className="colab-bold">
            {_.get(
              BOOKING_VERIFICATION_METHOD,
              verificationMethod,
              verificationMethod
            )}
          </div>
        </div>
        <div className="TenantsBox__bookingInfo__container">
          <div className="TenantsBox__bookingInfo__heading">Booker</div>
          <div className="colab-bold">{primaryTenant.fullName}</div>
        </div>
        {guarantor && (
          <div className="TenantsBox__bookingInfo__container">
            <div className="TenantsBox__bookingInfo__heading">Guarantor</div>
            <div className="colab-bold">{guarantor.fullName}</div>
          </div>
        )}
        <div className="TenantsBox__bookingInfo__container">
          <div className="TenantsBox__bookingInfo__heading">Rent per week</div>
          <div>£{rentPerWeek}</div>
        </div>
        <div className="TenantsBox__bookingInfo__container">
          <div className="TenantsBox__bookingInfo__heading">Total value</div>
          <div>£{totalValue}</div>
        </div>
      </div>
      <div className="TenantsBox__buttons">
        <ButtonDropdown title="Update Booking">
          <ButtonDropdown.Option
            onClick={undoCheckInTenants}
            disabled={_.isNil(checkedInAt)}>
            Undo check in
          </ButtonDropdown.Option>
          <VisibleIf
            permission={PERMISSIONS.CREATE_CHECK_OUT}
            locationRef={locationRef}>
            <ButtonDropdown.Option
              onClick={checkOutBookingEarly}
              disabled={!bookingCanBeCheckedOutEarly}>
              Early check out
            </ButtonDropdown.Option>
            <ButtonDropdown.Option
              onClick={undoBookingEarlyCheckout}
              disabled={!bookingEarlyCheckoutCanBeUndone}>
              Undo early check out
            </ButtonDropdown.Option>
          </VisibleIf>
          <ButtonDropdown.Option
            onClick={cancelBooking}
            disabled={!bookingCanBeCancelled}>
            Cancel booking
          </ButtonDropdown.Option>
        </ButtonDropdown>
      </div>
    </div>
  );
};

export default TenantsBox;
